import { useState } from 'react'
import { Link, useLoaderData } from 'react-router'
import { UserProfile } from '#app/components//user-profile'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '#app/components/ui/collapsible'
import { Icon, type IconName } from '#app/components/ui/icon'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubItem,
	SidebarSeparator,
	useSidebar,
} from '#app/components/ui/sidebar'
import { type loader } from '#app/root.tsx'
import { ThemeSwitch } from '#app/routes/_app+/resources+/theme-switch.tsx'
import { useOptionalAdminUser } from '#app/utils/user.ts'
import documents from '#content/documents.ts'
import minutes from '#content/minutes.ts'

export function MainSidebar() {
	const { schedules, requestInfo } = useLoaderData<typeof loader>()
	const userIsAdmin = useOptionalAdminUser() ?? false
	const [open, setOpen] = useState('irrigation')
	const handleOpenChange = (value: string) => {
		if (open === value) setOpen('')
		else setOpen(value)
	}

	const { setOpenMobile } = useSidebar()

	const membersItems: {
		title: string
		to: string
		icon: IconName
	}[] = [
		{
			title: 'Members Balances',
			to: '/members',
			icon: 'user-search',
		},
		{
			title: 'Transactions',
			to: '/members/transactions',
			icon: 'reader',
		},
		{
			title: 'Contact List',
			to: '/members/contacts',
			icon: 'avatar',
		},
		{
			title: 'Restrictions',
			to: '/members/restrictions',
			icon: 'passkey',
		},
	]

	return (
		<Sidebar collapsible="offcanvas">
			<SidebarHeader className="bg-secondary border-primary mb-2 flex flex-col items-center border-b-2 px-2 py-4">
				<div className="mx-2 flex flex-row items-center leading-snug">
					<span className="text-xl font-bold">Clearwater Farms&nbsp;</span>
					<span className="text-lg font-light text-nowrap">Unit 1</span>
				</div>
			</SidebarHeader>
			<SidebarContent>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton asChild size="xl">
							<Link to="/" onClick={() => setOpenMobile(false)}>
								<Icon name="home" />
								Home
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
				<Collapsible
					className="Irrigation/collapsible"
					open={open === 'irrigation'}
					onOpenChange={() => handleOpenChange('irrigation')}
				>
					<CollapsibleTrigger asChild>
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton className="hover:cursor-pointer" size="xl">
									<Icon name="droplets" size="md">
										<h1 className="text-lg">Irrigation</h1>
									</Icon>
								</SidebarMenuButton>
							</SidebarMenuItem>
						</SidebarMenu>
					</CollapsibleTrigger>
					<CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all">
						<SidebarGroup>
							<SidebarGroupContent>
								<SidebarMenu>
									<SidebarMenuItem>
										<SidebarMenuSub>
											<SidebarMenuSubItem>
												<SidebarMenuButton asChild>
													<Link
														to="/schedules"
														onClick={() => setOpenMobile(false)}
													>
														Irrigation Schedules
													</Link>
												</SidebarMenuButton>
											</SidebarMenuSubItem>
										</SidebarMenuSub>
										<SidebarMenuSub>
											<SidebarMenuSubItem>
												<SidebarMenuButton asChild>
													<Link
														to="/irrigation"
														onClick={() => setOpenMobile(false)}
													>
														<p className="text-sky-800">Where is the water?</p>
													</Link>
												</SidebarMenuButton>
											</SidebarMenuSubItem>
										</SidebarMenuSub>
										{[schedules?.previous, schedules?.current].map(
											(schedule, index) => (
												<SidebarMenuItem key={schedule?.date} className="mt-2">
													<Icon name="droplet">
														<h2
															className={`p-1 font-semibold ${index ? 'text-green-700' : 'text-red-700'}`}
														>
															{schedule?.date}
														</h2>
													</Icon>
													<SidebarSeparator />
													<SidebarMenuSub>
														<SidebarMenuSubItem>
															<SidebarMenuButton asChild>
																<Link
																	to={`/schedule/${schedule?.date}/timeline`}
																	onClick={() => setOpenMobile(false)}
																>
																	<p>{`${index ? 'Current' : 'Previous'} Timeline`}</p>
																</Link>
															</SidebarMenuButton>
														</SidebarMenuSubItem>
													</SidebarMenuSub>
													<SidebarMenuSub>
														<SidebarMenuSubItem>
															<SidebarMenuButton asChild>
																<Link
																	to={`schedule/${schedule?.date}/crossovers`}
																	onClick={() => setOpenMobile(false)}
																>
																	<p className="text-yellow-700">
																		Gate Changes & Crossovers
																	</p>
																</Link>
															</SidebarMenuButton>
														</SidebarMenuSubItem>
													</SidebarMenuSub>
													<SidebarMenuSub>
														<SidebarMenuSubItem>
															<SidebarMenuButton asChild>
																<Link
																	to={`documents/schedules/${schedule?.date}`}
																	onClick={() => setOpenMobile(false)}
																>
																	Schedule Notes
																</Link>
															</SidebarMenuButton>
														</SidebarMenuSubItem>
													</SidebarMenuSub>
												</SidebarMenuItem>
											),
										)}
										<SidebarMenuItem
											key={schedules.signup?.date}
											className="mt-2"
										>
											<Icon name="droplet">
												<h2 className="text-md p-1 font-semibold text-blue-700">
													{schedules.signup?.date}
												</h2>
											</Icon>
											<SidebarSeparator />
											<SidebarMenuSub>
												<SidebarMenuSubItem>
													<SidebarMenuButton asChild>
														<Link
															to={`/schedule/${schedules?.signup?.date}/signup`}
															onClick={() => setOpenMobile(false)}
														>
															{schedules?.signup
																? 'Sign Up Sheet'
																: 'No Schedules Open'}
														</Link>
													</SidebarMenuButton>
												</SidebarMenuSubItem>
											</SidebarMenuSub>
										</SidebarMenuItem>
									</SidebarMenuItem>
								</SidebarMenu>
							</SidebarGroupContent>
						</SidebarGroup>
					</CollapsibleContent>
				</Collapsible>

				<Collapsible
					className="Documents/collapsible"
					open={open === 'documents'}
					onOpenChange={() => handleOpenChange('documents')}
				>
					<CollapsibleTrigger asChild>
						<SidebarMenuButton className="hover:cursor-pointer" size="xl">
							<Icon name="file-text" size="md">
								<h1 className="text-lg">Documents</h1>
							</Icon>
						</SidebarMenuButton>
					</CollapsibleTrigger>
					<CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all">
						<SidebarGroup>
							<SidebarGroupContent>
								{documents.map((doc) => (
									<SidebarMenuSub key={doc.title}>
										<SidebarMenuSubItem>
											<SidebarMenuButton asChild>
												<Link
													to={doc.href}
													onClick={() => setOpenMobile(false)}
												>
													{doc.title}
												</Link>
											</SidebarMenuButton>
										</SidebarMenuSubItem>
									</SidebarMenuSub>
								))}
							</SidebarGroupContent>
						</SidebarGroup>
					</CollapsibleContent>
				</Collapsible>

				<Collapsible
					className="Meetings/collapsible"
					open={open === 'minutes'}
					onOpenChange={() => handleOpenChange('minutes')}
				>
					<CollapsibleTrigger asChild>
						<SidebarMenuButton className="hover:cursor-pointer" size="xl">
							<Icon name="calendar" size="md">
								<h1 className="text-lg">Meetings</h1>
							</Icon>
						</SidebarMenuButton>
					</CollapsibleTrigger>
					<CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all">
						<SidebarGroup>
							<SidebarGroupContent>
								{minutes
									.filter(({ executive }) => !executive || userIsAdmin)
									.map((mtg) => (
										<SidebarMenuSub key={mtg.title}>
											<SidebarMenuSubItem>
												<SidebarMenuButton asChild>
													<Link
														to={mtg.href}
														onClick={() => setOpenMobile(false)}
													>
														{mtg.title}:{' '}
														{mtg.executive ? 'Executive' : 'General'}
													</Link>
												</SidebarMenuButton>
											</SidebarMenuSubItem>
										</SidebarMenuSub>
									))}
							</SidebarGroupContent>
						</SidebarGroup>
					</CollapsibleContent>
				</Collapsible>

				{userIsAdmin ? (
					<Collapsible
						className="Members/collapsible"
						open={open === 'members'}
						onOpenChange={() => handleOpenChange('members')}
					>
						<CollapsibleTrigger asChild>
							<SidebarMenuButton className="hover:cursor-pointer" size="xl">
								<Icon name="users" size="md">
									<h1 className="text-lg">Members</h1>
								</Icon>
							</SidebarMenuButton>
						</CollapsibleTrigger>
						<CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden transition-all">
							<SidebarGroup>
								<SidebarGroupContent>
									{membersItems.map((link) => (
										<SidebarMenuSub key={link.title}>
											<SidebarMenuSubItem>
												<SidebarMenuButton asChild>
													<Link
														to={link.to}
														onClick={() => setOpenMobile(false)}
													>
														<Icon name={link.icon} size="md" />
														{link.title}
													</Link>
												</SidebarMenuButton>
											</SidebarMenuSubItem>
										</SidebarMenuSub>
									))}
								</SidebarGroupContent>
							</SidebarGroup>
						</CollapsibleContent>
					</Collapsible>
				) : null}
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton asChild size="xl">
							<Link to="/trade-list" onClick={() => setOpenMobile(false)}>
								<Icon name="id-card" />
								Trades & Resources
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton asChild size="xl">
							<Link to="/contact-us" onClick={() => setOpenMobile(false)}>
								<Icon name="envelope-closed" />
								Contact Us
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarContent>
			<SidebarFooter>
				<SidebarMenu className="mr-2">
					<SidebarMenuItem className="flex flex-row gap-1.5">
						<ThemeSwitch
							id="sidebar-theme"
							userPreference={requestInfo.userPrefs.theme}
						/>
						<UserProfile />
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>
		</Sidebar>
	)
}
